import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { DEATH_EXPLOSIONS, NITRO_EFFECTS } from '../../src/game/data.js';
import {
  inspectOneShotSample,
  inspectSustainedSample,
  previewOneShotSample,
  previewSustainedSample,
} from './proceduralSound.js';

const AUTO_NITRO_STORAGE_KEY='toybox-catalog-studio:nitro-auto-rotate';
const AUTO_EXPLOSION_STORAGE_KEY='toybox-catalog-studio:explosion-auto-repeat';
const EFFECT_STYLES={
  nitro:new Set(['flame','plasma','confetti']),
  explosion:new Set(['burst','plasma','firework','confetti']),
};
const SOUND_WAVES=new Set(['sine','triangle','sawtooth','square']);
const SOUND_FILTERS=new Set(['lowpass','bandpass','highpass']);

const $=(root,selector)=>root.querySelector(selector);
const $$=(root,selector)=>[...root.querySelectorAll(selector)];

function cleanId(value,fallback){
  return String(value||fallback)
    .normalize('NFD').replace(/[\u0300-\u036f]/g,'')
    .replace(/[^a-zA-Z0-9_-]+/g,'-')
    .replace(/^-+|-+$/g,'')
    .toLowerCase()||fallback;
}

function readBooleanPreference(key){
  try{
    const stored=window.localStorage.getItem(key);
    return stored===null?true:stored==='true';
  }catch{
    return true;
  }
}

function saveBooleanPreference(key,enabled){
  try{
    window.localStorage.setItem(key,String(enabled));
  }catch{
    // La vista continúa funcionando si el navegador bloquea localStorage.
  }
}

function disposeObject(object){
  object?.traverse((child)=>{
    if(!child.isMesh)return;
    child.geometry?.dispose();
    const materials=Array.isArray(child.material)?child.material:[child.material];
    materials.filter(Boolean).forEach((material)=>material.dispose?.());
  });
}

function nitroDefaults(style){
  if(style==='plasma'){
    return {
      trailLength:1.75,
      trailWidth:.18,
      particleCount:34,
      particleSize:.075,
      spread:.24,
      speed:3.2,
      pulse:.32,
      nozzleCount:2,
      nozzleGap:.34,
      glowOpacity:.88,
      soundWave:'sine',
      soundFilterType:'bandpass',
      soundTone:330,
      soundFilter:1550,
      soundNoiseGain:.07,
      soundToneGain:.025,
      soundPulse:7,
      soundIgnitionGain:.065,
    };
  }
  if(style==='confetti'){
    return {
      trailLength:1.35,
      trailWidth:.09,
      particleCount:46,
      particleSize:.07,
      spread:.62,
      speed:2.5,
      pulse:.48,
      nozzleCount:3,
      nozzleGap:.30,
      glowOpacity:.72,
      soundWave:'triangle',
      soundFilterType:'highpass',
      soundTone:540,
      soundFilter:2150,
      soundNoiseGain:.064,
      soundToneGain:.018,
      soundPulse:25,
      soundIgnitionGain:.065,
    };
  }
  return {
    trailLength:1.55,
    trailWidth:.14,
    particleCount:30,
    particleSize:.06,
    spread:.34,
    speed:3,
    pulse:.22,
    nozzleCount:2,
    nozzleGap:.36,
    glowOpacity:.82,
    soundWave:'sawtooth',
    soundFilterType:'bandpass',
    soundTone:178,
    soundFilter:1120,
    soundNoiseGain:.075,
    soundToneGain:.024,
    soundPulse:12,
    soundIgnitionGain:.065,
  };
}

function cloneNitroDesign(source={}){
  const style=EFFECT_STYLES.nitro.has(source.style)?source.style:'flame';
  const defaults=nitroDefaults(style);
  const soundDefaults=source.id==='hot_flames'?{
    soundWave:'sawtooth',
    soundFilterType:'lowpass',
    soundTone:108,
    soundFilter:690,
    soundNoiseGain:.09,
    soundToneGain:.032,
    soundPulse:10,
    soundIgnitionGain:.065,
  }:defaults;
  return {
    style,
    color:source.color||'#5ef3ff',
    accent:source.accent||'#ffef75',
    spark:source.spark||source.color||'#66eaff',
    trailLength:Number(source.trailLength??defaults.trailLength),
    trailWidth:Number(source.trailWidth??defaults.trailWidth),
    particleCount:Math.round(Number(source.particleCount??defaults.particleCount)),
    particleSize:Number(source.particleSize??defaults.particleSize),
    spread:Number(source.spread??defaults.spread),
    speed:Number(source.speed??defaults.speed),
    pulse:Number(source.pulse??defaults.pulse),
    nozzleCount:Math.round(Number(source.nozzleCount??defaults.nozzleCount)),
    nozzleGap:Number(source.nozzleGap??defaults.nozzleGap),
    glowOpacity:Number(source.glowOpacity??defaults.glowOpacity),
    soundWave:SOUND_WAVES.has(source.soundWave)?source.soundWave:soundDefaults.soundWave,
    soundFilterType:SOUND_FILTERS.has(source.soundFilterType)
      ?source.soundFilterType
      :soundDefaults.soundFilterType,
    soundTone:Number(source.soundTone??soundDefaults.soundTone),
    soundFilter:Number(source.soundFilter??soundDefaults.soundFilter),
    soundNoiseGain:Number(source.soundNoiseGain??soundDefaults.soundNoiseGain),
    soundToneGain:Number(source.soundToneGain??soundDefaults.soundToneGain),
    soundPulse:Number(source.soundPulse??soundDefaults.soundPulse),
    soundIgnitionGain:Number(source.soundIgnitionGain??soundDefaults.soundIgnitionGain),
    soundSampleVolume:Number(source.soundSampleVolume??.5),
    soundSampleRate:Number(source.soundSampleRate??1),
    sampleLoopStart:Number(source.sampleLoopStart??0),
    sampleLoopEndTrim:Number(source.sampleLoopEndTrim??0),
    sampleLoopCrossfade:Number(source.sampleLoopCrossfade??.04),
  };
}

function explosionDefaults(style){
  if(style==='plasma'){
    return {
      particleCount:30,
      power:6.5,
      particleSize:.13,
      duration:.9,
      gravity:.4,
      flashSize:.72,
      shockwaveSize:1.65,
      shockwaveThickness:.055,
      loopDelay:.65,
      soundWave:'sine',
      soundFilterType:'lowpass',
      soundPitch:235,
      soundDuration:.44,
      soundFilter:720,
      soundNoiseGain:.16,
      soundToneGain:.18,
      soundAccentGain:.055,
    };
  }
  if(style==='firework'){
    return {
      particleCount:42,
      power:8,
      particleSize:.075,
      duration:1.15,
      gravity:2.4,
      flashSize:.48,
      shockwaveSize:1.25,
      shockwaveThickness:.035,
      loopDelay:.55,
      soundWave:'square',
      soundFilterType:'highpass',
      soundPitch:112,
      soundDuration:.44,
      soundFilter:1450,
      soundNoiseGain:.16,
      soundToneGain:.18,
      soundAccentGain:.07,
    };
  }
  if(style==='confetti'){
    return {
      particleCount:52,
      power:5.5,
      particleSize:.095,
      duration:1.45,
      gravity:3.8,
      flashSize:.48,
      shockwaveSize:1.3,
      shockwaveThickness:.045,
      loopDelay:.55,
      soundWave:'sawtooth',
      soundFilterType:'lowpass',
      soundPitch:155,
      soundDuration:.44,
      soundFilter:520,
      soundNoiseGain:.1,
      soundToneGain:.18,
      soundAccentGain:.045,
    };
  }
  return {
    particleCount:30,
    power:5.5,
    particleSize:.11,
    duration:.95,
    gravity:3,
    flashSize:.5,
    shockwaveSize:1.4,
    shockwaveThickness:.05,
    loopDelay:.6,
    soundWave:'sawtooth',
    soundFilterType:'lowpass',
    soundPitch:112,
    soundDuration:.44,
    soundFilter:520,
    soundNoiseGain:.16,
    soundToneGain:.18,
    soundAccentGain:0,
  };
}

function cloneExplosionDesign(source={}){
  const style=EFFECT_STYLES.explosion.has(source.style)?source.style:'burst';
  const defaults=explosionDefaults(style);
  return {
    style,
    color:source.color||'#ffd83d',
    accent:source.accent||'#ff742f',
    particleCount:Math.round(Number(source.particleCount??defaults.particleCount)),
    power:Number(source.power??defaults.power),
    particleSize:Number(source.particleSize??defaults.particleSize),
    duration:Number(source.duration??defaults.duration),
    gravity:Number(source.gravity??defaults.gravity),
    flashSize:Number(source.flashSize??defaults.flashSize),
    shockwaveSize:Number(source.shockwaveSize??defaults.shockwaveSize),
    shockwaveThickness:Number(source.shockwaveThickness??defaults.shockwaveThickness),
    loopDelay:Number(source.loopDelay??defaults.loopDelay),
    soundWave:SOUND_WAVES.has(source.soundWave)?source.soundWave:defaults.soundWave,
    soundFilterType:SOUND_FILTERS.has(source.soundFilterType)
      ?source.soundFilterType
      :defaults.soundFilterType,
    soundPitch:Number(source.soundPitch??defaults.soundPitch),
    soundDuration:Number(source.soundDuration??defaults.soundDuration),
    soundFilter:Number(source.soundFilter??defaults.soundFilter),
    soundNoiseGain:Number(source.soundNoiseGain??defaults.soundNoiseGain),
    soundToneGain:Number(source.soundToneGain??defaults.soundToneGain),
    soundAccentGain:Number(source.soundAccentGain??defaults.soundAccentGain),
    soundSampleVolume:Number(source.soundSampleVolume??.5),
    soundSampleRate:Number(source.soundSampleRate??1),
  };
}

let previewAudioContext=null;
let previewAudioStops=[];

async function getPreviewAudioContext(){
  const AudioContextClass=window.AudioContext||window.webkitAudioContext;
  if(!AudioContextClass)throw new Error('Este navegador no permite previsualizar audio.');
  if(!previewAudioContext)previewAudioContext=new AudioContextClass();
  if(previewAudioContext.state==='suspended')await previewAudioContext.resume();
  previewAudioStops.forEach((stop)=>{
    try{stop();}catch{
      // El nodo ya había finalizado.
    }
  });
  previewAudioStops=[];
  return previewAudioContext;
}

function createNoiseBuffer(context,duration){
  const length=Math.ceil(context.sampleRate*duration);
  const buffer=context.createBuffer(1,length,context.sampleRate);
  const data=buffer.getChannelData(0);
  for(let index=0;index<data.length;index+=1)data[index]=Math.random()*2-1;
  return buffer;
}

function connectPreviewMaster(context){
  const master=context.createGain();
  master.gain.value=.72;
  master.connect(context.destination);
  return master;
}

async function previewNitroSound(design){
  const context=await getPreviewAudioContext();
  const now=context.currentTime;
  const duration=1.25;
  const master=connectPreviewMaster(context);
  const noise=context.createBufferSource();
  noise.buffer=createNoiseBuffer(context,duration+.08);
  const filter=context.createBiquadFilter();
  filter.type=design.soundFilterType;
  filter.frequency.setValueAtTime(design.soundFilter,now);
  const noiseGain=context.createGain();
  noiseGain.gain.setValueAtTime(.001,now);
  noiseGain.gain.exponentialRampToValueAtTime(
    Math.max(.001,design.soundNoiseGain),
    now+.08,
  );
  noiseGain.gain.setValueAtTime(Math.max(.001,design.soundNoiseGain),now+duration-.18);
  noiseGain.gain.exponentialRampToValueAtTime(.001,now+duration);
  noise.connect(filter);
  filter.connect(noiseGain);
  noiseGain.connect(master);

  const oscillator=context.createOscillator();
  oscillator.type=design.soundWave;
  oscillator.frequency.setValueAtTime(design.soundTone*1.7,now);
  oscillator.frequency.exponentialRampToValueAtTime(design.soundTone,now+.13);
  const toneGain=context.createGain();
  toneGain.gain.setValueAtTime(.001,now);
  toneGain.gain.exponentialRampToValueAtTime(
    Math.max(.001,design.soundIgnitionGain),
    now+.06,
  );
  toneGain.gain.exponentialRampToValueAtTime(
    Math.max(.001,design.soundToneGain),
    now+.18,
  );
  toneGain.gain.setValueAtTime(Math.max(.001,design.soundToneGain),now+duration-.16);
  toneGain.gain.exponentialRampToValueAtTime(.001,now+duration);
  oscillator.connect(toneGain);
  toneGain.connect(master);
  oscillator.start(now);
  noise.start(now);
  oscillator.stop(now+duration+.02);
  noise.stop(now+duration+.02);
  previewAudioStops=[
    ()=>oscillator.stop(),
    ()=>noise.stop(),
  ];
}

async function previewExplosionSound(design){
  const context=await getPreviewAudioContext();
  const now=context.currentTime;
  const duration=design.soundDuration;
  const master=connectPreviewMaster(context);
  const oscillator=context.createOscillator();
  oscillator.type=design.soundWave;
  oscillator.frequency.setValueAtTime(design.soundPitch,now);
  oscillator.frequency.exponentialRampToValueAtTime(
    Math.max(28,design.soundPitch*.28),
    now+duration,
  );
  const toneGain=context.createGain();
  toneGain.gain.setValueAtTime(Math.max(.001,design.soundToneGain),now);
  toneGain.gain.exponentialRampToValueAtTime(.001,now+duration);
  oscillator.connect(toneGain);
  toneGain.connect(master);

  const noise=context.createBufferSource();
  noise.buffer=createNoiseBuffer(context,duration+.08);
  const filter=context.createBiquadFilter();
  filter.type=design.soundFilterType;
  filter.frequency.setValueAtTime(design.soundFilter,now);
  filter.frequency.exponentialRampToValueAtTime(
    Math.max(70,design.soundFilter*.42),
    now+duration,
  );
  const noiseGain=context.createGain();
  noiseGain.gain.setValueAtTime(Math.max(.001,design.soundNoiseGain),now);
  noiseGain.gain.exponentialRampToValueAtTime(.001,now+duration);
  noise.connect(filter);
  filter.connect(noiseGain);
  noiseGain.connect(master);

  const accent=context.createOscillator();
  accent.type=design.style==='firework'?'square':'triangle';
  accent.frequency.setValueAtTime(design.soundPitch*3.4,now+.015);
  accent.frequency.exponentialRampToValueAtTime(
    Math.max(45,design.soundPitch*1.4),
    now+Math.min(.22,duration),
  );
  const accentGain=context.createGain();
  accentGain.gain.setValueAtTime(.001,now);
  accentGain.gain.linearRampToValueAtTime(
    Math.max(.001,design.soundAccentGain),
    now+.018,
  );
  accentGain.gain.exponentialRampToValueAtTime(
    .001,
    now+Math.min(.24,duration),
  );
  accent.connect(accentGain);
  accentGain.connect(master);
  oscillator.start(now);
  noise.start(now);
  accent.start(now);
  oscillator.stop(now+duration+.02);
  noise.stop(now+duration+.02);
  accent.stop(now+Math.min(.25,duration)+.02);
  previewAudioStops=[
    ()=>oscillator.stop(),
    ()=>noise.stop(),
    ()=>accent.stop(),
  ];
}

function particleGeometry(style,size){
  if(style==='confetti')return new THREE.BoxGeometry(size*.48,size*1.45,size*.16);
  if(style==='firework')return new THREE.OctahedronGeometry(size*.72,0);
  if(style==='plasma')return new THREE.SphereGeometry(size,8,6);
  return new THREE.IcosahedronGeometry(size,0);
}

function createNitroPreview(design){
  const root=new THREE.Group();
  root.name='procedural_nitro_preview';
  const emitterMaterial=new THREE.MeshPhysicalMaterial({
    color:'#434254',
    metalness:.72,
    roughness:.3,
    clearcoat:.45,
  });
  const rimMaterial=new THREE.MeshPhysicalMaterial({
    color:design.accent,
    emissive:design.accent,
    emissiveIntensity:.28,
    metalness:.45,
    roughness:.26,
  });
  const emitter=new THREE.Mesh(new THREE.BoxGeometry(
    Math.max(.72,design.nozzleGap*(design.nozzleCount-1)+.54),
    .28,
    .34,
  ),emitterMaterial);
  emitter.position.set(0,.6,-.22);
  emitter.castShadow=true;
  root.add(emitter);

  const flames=[];
  const nozzlePositions=[];
  for(let index=0;index<design.nozzleCount;index+=1){
    const x=(index-(design.nozzleCount-1)*.5)*design.nozzleGap;
    nozzlePositions.push(x);
    const nozzle=new THREE.Mesh(
      new THREE.CylinderGeometry(design.trailWidth*.72,design.trailWidth,.22,18),
      rimMaterial,
    );
    nozzle.rotation.x=Math.PI/2;
    nozzle.position.set(x,.6,.02);
    nozzle.castShadow=true;
    root.add(nozzle);

    const flameMaterial=new THREE.MeshBasicMaterial({
      color:index%2?design.color:design.accent,
      transparent:true,
      opacity:design.glowOpacity,
      depthWrite:false,
      blending:THREE.AdditiveBlending,
    });
    const flame=new THREE.Mesh(
      new THREE.ConeGeometry(design.trailWidth,design.trailLength,16,1,true),
      flameMaterial,
    );
    flame.rotation.x=Math.PI/2;
    flame.position.set(x,.6,.12+design.trailLength*.5);
    root.add(flame);
    flames.push(flame);

    if(design.style==='plasma'){
      const core=new THREE.Mesh(
        new THREE.ConeGeometry(design.trailWidth*.42,design.trailLength*.72,14,1,true),
        new THREE.MeshBasicMaterial({
          color:design.accent,
          transparent:true,
          opacity:Math.min(1,design.glowOpacity+.1),
          depthWrite:false,
          blending:THREE.AdditiveBlending,
        }),
      );
      core.rotation.x=Math.PI/2;
      core.position.set(x,.6,.12+design.trailLength*.36);
      root.add(core);
      flames.push(core);
    }
  }

  const particles=[];
  const palette=[design.color,design.accent,design.spark];
  for(let index=0;index<design.particleCount;index+=1){
    const geometry=particleGeometry(design.style,design.particleSize);
    const color=palette[index%palette.length];
    const material=new THREE.MeshBasicMaterial({
      color,
      transparent:true,
      opacity:.9,
      depthWrite:false,
      blending:design.style==='confetti'?THREE.NormalBlending:THREE.AdditiveBlending,
    });
    const mesh=new THREE.Mesh(geometry,material);
    root.add(mesh);
    const particle={
      mesh,
      nozzleIndex:index%nozzlePositions.length,
      phase:Math.random()*Math.PI*2,
      wobble:.65+Math.random()*1.6,
      age:0,
      life:1,
      speed:design.speed,
    };
    particles.push(particle);
    resetNitroParticle(particle,design,nozzlePositions,true);
  }
  root.userData.effect={flames,particles,nozzlePositions,time:0};
  return root;
}

function resetNitroParticle(particle,design,nozzlePositions,initial=false){
  particle.life=(design.trailLength/design.speed)*(.75+Math.random()*.48);
  particle.age=initial?Math.random()*particle.life:0;
  particle.speed=design.speed*(.78+Math.random()*.44);
  particle.mesh.position.set(
    nozzlePositions[particle.nozzleIndex]+(Math.random()-.5)*design.spread*.18,
    .6+(Math.random()-.5)*design.spread*.18,
    .14+particle.age*particle.speed,
  );
  particle.mesh.rotation.set(
    Math.random()*Math.PI,
    Math.random()*Math.PI,
    Math.random()*Math.PI,
  );
}

function updateNitroPreview(root,design,dt,time){
  const effect=root?.userData.effect;
  if(!effect)return;
  effect.time+=dt;
  const pulse=1+Math.sin(time*(6+design.pulse*14))*design.pulse*.08;
  effect.flames.forEach((flame,index)=>{
    flame.scale.set(
      pulse*(1+Math.sin(time*11+index)*.035),
      1,
      pulse,
    );
    flame.material.opacity=design.glowOpacity*(.88+Math.sin(time*13+index)*.1);
  });
  effect.particles.forEach((particle)=>{
    particle.age+=dt;
    if(particle.age>=particle.life){
      resetNitroParticle(particle,design,effect.nozzlePositions);
      return;
    }
    const ratio=particle.age/particle.life;
    particle.mesh.position.z+=particle.speed*dt;
    particle.mesh.position.x+=Math.sin(
      time*7*particle.wobble+particle.phase,
    )*design.spread*dt*.35;
    particle.mesh.position.y+=Math.cos(
      time*6*particle.wobble+particle.phase,
    )*design.spread*dt*.28;
    particle.mesh.material.opacity=Math.max(0,(1-ratio)*.92);
    const scale=.65+ratio*.8;
    particle.mesh.scale.setScalar(scale);
    if(design.style==='confetti'){
      particle.mesh.rotation.x+=dt*(4+particle.wobble);
      particle.mesh.rotation.z+=dt*(6+particle.wobble);
    }
  });
}

function randomExplosionVelocity(design){
  const angle=Math.random()*Math.PI*2;
  const elevation=design.style==='firework'
    ? .12+Math.random()*.72
    :design.style==='confetti'
      ?.25+Math.random()*.72
      :-.12+Math.random()*.82;
  const horizontal=Math.cos(elevation);
  const speed=design.power*(.48+Math.random()*.58);
  return new THREE.Vector3(
    Math.cos(angle)*horizontal*speed,
    Math.sin(elevation)*speed,
    Math.sin(angle)*horizontal*speed,
  );
}

function resetExplosionPreview(root,design){
  const effect=root?.userData.effect;
  if(!effect)return;
  effect.elapsed=0;
  effect.particles.forEach((particle)=>{
    particle.mesh.visible=true;
    particle.mesh.position.copy(effect.origin);
    particle.mesh.scale.setScalar(.55+Math.random()*.7);
    particle.mesh.material.opacity=1;
    particle.mesh.rotation.set(
      Math.random()*Math.PI,
      Math.random()*Math.PI,
      Math.random()*Math.PI,
    );
    particle.velocity.copy(randomExplosionVelocity(design));
    particle.spin.set(
      (Math.random()-.5)*12,
      (Math.random()-.5)*12,
      (Math.random()-.5)*12,
    );
  });
  effect.flash.visible=true;
  effect.flash.material.opacity=.76;
  effect.flash.scale.setScalar(design.flashSize);
  effect.ring.visible=true;
  effect.ring.material.opacity=.72;
  effect.ring.scale.setScalar(.08);
}

function createExplosionPreview(design){
  const root=new THREE.Group();
  root.name='procedural_explosion_preview';
  const origin=new THREE.Vector3(0,.68,0);
  const particles=[];
  for(let index=0;index<design.particleCount;index+=1){
    const color=index%2?design.color:design.accent;
    const mesh=new THREE.Mesh(
      particleGeometry(design.style,design.particleSize),
      new THREE.MeshBasicMaterial({
        color,
        transparent:true,
        opacity:1,
        depthWrite:false,
        blending:design.style==='confetti'?THREE.NormalBlending:THREE.AdditiveBlending,
      }),
    );
    root.add(mesh);
    particles.push({
      mesh,
      velocity:new THREE.Vector3(),
      spin:new THREE.Vector3(),
    });
  }
  const flash=new THREE.Mesh(
    new THREE.SphereGeometry(1,18,12),
    new THREE.MeshBasicMaterial({
      color:design.accent,
      transparent:true,
      opacity:.76,
      depthWrite:false,
      blending:THREE.AdditiveBlending,
    }),
  );
  flash.position.copy(origin);
  root.add(flash);
  const ring=new THREE.Mesh(
    new THREE.TorusGeometry(.5,design.shockwaveThickness,10,48),
    new THREE.MeshBasicMaterial({
      color:design.color,
      transparent:true,
      opacity:.72,
      depthWrite:false,
      blending:THREE.AdditiveBlending,
    }),
  );
  ring.position.copy(origin);
  ring.rotation.x=Math.PI/2;
  root.add(ring);
  root.userData.effect={particles,flash,ring,origin,elapsed:0};
  resetExplosionPreview(root,design);
  return root;
}

function updateExplosionPreview(root,design,dt,autoRepeat){
  const effect=root?.userData.effect;
  if(!effect)return;
  effect.elapsed+=dt;
  if(effect.elapsed>design.duration){
    effect.particles.forEach((particle)=>{particle.mesh.visible=false;});
    effect.flash.visible=false;
    effect.ring.visible=false;
    if(autoRepeat&&effect.elapsed>design.duration+design.loopDelay){
      resetExplosionPreview(root,design);
    }
    return;
  }
  const ratio=effect.elapsed/design.duration;
  effect.particles.forEach((particle)=>{
    particle.velocity.y-=design.gravity*dt;
    particle.mesh.position.addScaledVector(particle.velocity,dt);
    particle.mesh.rotation.x+=particle.spin.x*dt;
    particle.mesh.rotation.y+=particle.spin.y*dt;
    particle.mesh.rotation.z+=particle.spin.z*dt;
    particle.mesh.material.opacity=Math.max(0,1-ratio);
    if(design.style==='plasma'){
      particle.mesh.scale.multiplyScalar(1+dt*1.8);
    }
  });
  effect.flash.material.opacity=Math.max(0,(1-ratio*3)*.76);
  effect.flash.scale.setScalar(design.flashSize*(1+ratio*2.2));
  effect.ring.material.opacity=Math.max(0,(1-ratio)*.72);
  effect.ring.scale.setScalar(
    .08+(design.shockwaveSize/.5-.08)*(1-Math.pow(1-ratio,2)),
  );
}

class AnimatedEffectEditor{
  constructor(screen,config,callbacks={}){
    this.screen=screen;
    this.config=config;
    this.callbacks=callbacks;
    this.prefix=config.prefix;
    this.canvas=$(screen,`#${this.prefix}-editor-canvas`);
    this.status=$(screen,`#${this.prefix}-editor-message`);
    this.saveButton=$(screen,`#${this.prefix}-editor-save`);
    this.templateSelect=$(screen,`#${this.prefix}-editor-template`);
    this.styleSelect=$(screen,`#${this.prefix}-editor-style`);
    this.soundFileInput=$(screen,`#${this.prefix}-editor-sound-file`);
    this.soundFileName=$(screen,`#${this.prefix}-editor-sound-file-name`);
    this.soundFileInfo=$(screen,`#${this.prefix}-editor-sound-file-info`);
    this.soundButton=$(screen,`#${this.prefix}-editor-test-sound`);
    this.autoInput=$(screen,config.autoSelector);
    this.autoInput.checked=readBooleanPreference(config.autoStorageKey);
    this.design=config.cloneDesign(config.catalog[0]);
    this.soundMode='procedural';
    this.soundFile=null;
    this.soundStats=null;
    this.active=false;
    this.clock=new THREE.Clock();
    this.initScene();
    this.bindInterface();
    this.syncControls();
    this.rebuild();
    this.animate();
  }

  initScene(){
    this.renderer=new THREE.WebGLRenderer({
      canvas:this.canvas,
      antialias:true,
      powerPreference:'high-performance',
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio,1.75));
    this.renderer.outputColorSpace=THREE.SRGBColorSpace;
    this.renderer.toneMapping=THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure=1.08;
    this.renderer.shadowMap.enabled=true;

    this.scene=new THREE.Scene();
    this.scene.background=new THREE.Color('#17172a');
    this.scene.fog=new THREE.Fog('#17172a',10,22);
    this.camera=new THREE.PerspectiveCamera(42,1,.03,50);
    this.camera.position.fromArray(this.config.cameraPosition);
    this.controls=new OrbitControls(this.camera,this.canvas);
    this.controls.enableDamping=true;
    this.controls.dampingFactor=.075;
    this.controls.target.fromArray(this.config.target);
    this.controls.minDistance=2;
    this.controls.maxDistance=10;
    this.scene.add(new THREE.HemisphereLight(0xe8f3ff,0x25213a,2.1));
    const key=new THREE.DirectionalLight(0xffedd3,4.2);
    key.position.set(-4,7,-5);
    key.castShadow=true;
    key.shadow.mapSize.set(1024,1024);
    this.scene.add(key);
    const fill=new THREE.DirectionalLight(0x63cfff,2.3);
    fill.position.set(5,3,4);
    this.scene.add(fill);

    const floor=new THREE.Mesh(
      new THREE.CircleGeometry(3,64),
      new THREE.MeshStandardMaterial({color:'#201c34',roughness:.9,metalness:.05}),
    );
    floor.rotation.x=-Math.PI/2;
    floor.receiveShadow=true;
    this.scene.add(floor);
    const grid=new THREE.GridHelper(7,20,0x4dddec,0x3b3551);
    grid.position.y=.006;
    grid.material.transparent=true;
    grid.material.opacity=.25;
    this.scene.add(grid);
    this.previewMount=new THREE.Group();
    this.scene.add(this.previewMount);
  }

  bindInterface(){
    $(this.screen,`#${this.prefix}-editor-close`).addEventListener('click',()=>this.close());
    $(this.screen,`#${this.prefix}-editor-reset`).addEventListener('click',()=>this.resetDesign());
    this.saveButton.addEventListener('click',()=>this.save());
    $$ (this.screen,`[data-${this.prefix}-sound-mode]`).forEach((button)=>{
      button.addEventListener('click',()=>{
        this.setSoundMode(button.dataset[`${this.prefix}SoundMode`]);
      });
    });
    this.soundFileInput?.addEventListener('change',()=>{
      this.loadSoundFile(this.soundFileInput.files[0]);
    });
    this.templateSelect.addEventListener('change',()=>{
      const template=this.config.catalog.find(
        (item)=>item.id===this.templateSelect.value,
      )||this.config.catalog[0];
      this.design=this.config.cloneDesign(template);
      this.syncControls();
      this.rebuild();
    });
    this.styleSelect.addEventListener('change',()=>{
      this.design=this.config.cloneDesign({
        ...this.design,
        style:this.styleSelect.value,
        ...this.config.styleDefaults(this.styleSelect.value),
      });
      this.syncControls();
      this.rebuild();
    });
    $$ (this.screen,`[data-${this.prefix}-design]`).forEach((input)=>{
      input.addEventListener('input',()=>{
        const key=input.dataset[`${this.prefix}Design`];
        this.design[key]=(input.type==='color'||input.tagName==='SELECT')
          ?input.value
          :Number(input.value);
        this.syncOutput(input);
        if(key.startsWith('sound')||key.startsWith('sample')){
          this.setStatus('Sonido actualizado. Usá “Probar sonido” para escucharlo.');
        }else{
          this.rebuild();
        }
      });
    });
    this.autoInput.addEventListener('change',()=>{
      saveBooleanPreference(this.config.autoStorageKey,this.autoInput.checked);
      if(this.config.autoMode==='repeat'&&this.autoInput.checked){
        this.replay();
      }
    });
    const replay=$(this.screen,`#${this.prefix}-editor-replay`);
    replay?.addEventListener('click',()=>this.replay());
    this.soundButton?.addEventListener('click',()=>this.testSound());
    window.addEventListener('resize',()=>this.resize());
  }

  syncOutput(input){
    input.syncNumberControl?.();
    const key=input.dataset[`${this.prefix}Design`];
    const output=$(this.screen,`[data-${this.prefix}-output="${key}"]`);
    if(!output)return;
    const value=this.design[key];
    const decimals=Number(input.step)<.01?3:2;
    output.textContent=Number.isInteger(value)?value:Number(value).toFixed(decimals);
  }

  syncControls(){
    $$ (this.screen,`[data-${this.prefix}-design]`).forEach((input)=>{
      input.value=this.design[input.dataset[`${this.prefix}Design`]];
      this.syncOutput(input);
    });
    this.styleSelect.value=this.design.style;
    $$ (this.screen,`[data-${this.prefix}-style-panel]`).forEach((panel)=>{
      const styles=panel.dataset[`${this.prefix}StylePanel`].split(/\s+/);
      panel.classList.toggle('hidden',!styles.includes(this.design.style));
    });
    $$ (this.screen,`[data-${this.prefix}-sound-mode]`).forEach((button)=>{
      button.classList.toggle(
        'selected',
        button.dataset[`${this.prefix}SoundMode`]===this.soundMode,
      );
    });
    $$ (this.screen,`[data-${this.prefix}-sound-panel]`).forEach((panel)=>{
      panel.classList.toggle(
        'hidden',
        panel.dataset[`${this.prefix}SoundPanel`]!==this.soundMode,
      );
    });
    this.updateActionButtons();
  }

  updateActionButtons(){
    const missingSound=this.soundMode==='mp3'&&!this.soundFile;
    this.saveButton.disabled=missingSound;
    if(this.soundButton)this.soundButton.disabled=missingSound;
  }

  setSoundMode(mode){
    if(mode!=='procedural'&&mode!=='mp3')return;
    this.soundMode=mode;
    this.syncControls();
    this.setStatus(
      mode==='mp3'
        ?this.soundFile
          ?'MP3 listo para probar.'
          :this.config.chooseSoundMessage
        :'Sonido procedural listo para probar.',
      mode==='mp3'&&this.soundFile?'success':'ready',
    );
  }

  async loadSoundFile(file,{applySuggestedTrim=true}={}){
    if(!file)return;
    this.saveButton.disabled=true;
    if(this.soundButton)this.soundButton.disabled=true;
    this.setStatus(this.config.loadingSoundMessage);
    try{
      const stats=this.config.loopSound
        ?await inspectSustainedSample(file)
        :await inspectOneShotSample(file);
      this.soundMode='mp3';
      this.soundFile=file;
      this.soundStats=stats;
      if(this.config.loopSound&&applySuggestedTrim){
        this.design.sampleLoopStart=stats.suggestedStartTrim||0;
        this.design.sampleLoopEndTrim=stats.suggestedEndTrim||0;
      }
      this.soundFileName.textContent=file.name;
      this.soundFileInfo.textContent=[
        `${stats.duration.toFixed(2)} s`,
        `${stats.sampleRate} Hz`,
        stats.channels===1?'mono':`${stats.channels} canales`,
        stats.warnings.length?'revisar':'listo',
      ].join(' · ');
      this.syncControls();
      this.setStatus(stats.warnings[0]||this.config.loadedSoundMessage,stats.warnings.length?'warning':'success');
    }catch(error){
      console.error(error);
      this.soundFile=null;
      this.soundStats=null;
      this.soundFileInput.value='';
      this.soundFileName.textContent='Elegir archivo MP3';
      this.soundFileInfo.textContent='No se pudo validar el archivo';
      this.syncControls();
      this.setStatus(error.message||'No se pudo abrir el MP3.','error');
    }
  }

  async testSound(){
    if(this.soundMode==='mp3'&&!this.soundFile){
      this.setStatus('Cargá un MP3 antes de probar el sonido.','warning');
      return;
    }
    this.soundButton.disabled=true;
    try{
      if(this.soundMode==='mp3'){
        if(this.config.loopSound)await previewSustainedSample(this.soundFile,this.design);
        else await previewOneShotSample(this.soundFile,this.design);
      }else{
        await this.config.testSound?.(this.design);
      }
      this.setStatus(
        this.soundMode==='mp3'?this.config.sampleSoundMessage:this.config.soundMessage,
        'success',
      );
    }catch(error){
      console.error(error);
      this.setStatus(error.message||'No se pudo reproducir el sonido.','error');
    }finally{
      this.updateActionButtons();
    }
  }

  replacePreview(object){
    if(this.previewObject){
      this.previewMount.remove(this.previewObject);
      disposeObject(this.previewObject);
    }
    this.previewObject=object;
    this.previewMount.add(object);
  }

  rebuild(){
    this.replacePreview(this.config.createPreview(this.design));
    this.setStatus(this.config.updatedMessage);
  }

  replay(){
    this.config.replay?.(this.previewObject,this.design);
    this.setStatus(this.config.replayMessage);
  }

  resetDesign(){
    const template=this.config.catalog.find(
      (item)=>item.id===this.templateSelect.value,
    )||this.config.catalog[0];
    this.design=this.config.cloneDesign(template);
    this.syncControls();
    this.rebuild();
  }

  clearWorkspace(){
    this.templateSelect.value=this.config.catalog[0].id;
    this.design=this.config.cloneDesign(this.config.catalog[0]);
    this.soundMode='procedural';
    this.soundFile=null;
    this.soundStats=null;
    if(this.soundFileInput)this.soundFileInput.value='';
    if(this.soundFileName)this.soundFileName.textContent='Elegir archivo MP3';
    if(this.soundFileInfo)this.soundFileInfo.textContent=this.config.defaultSoundFileInfo;
    this.syncControls();
    this.rebuild();
  }

  open(){
    this.active=true;
    this.clock.start();
    this.callbacks.onOpen?.();
    requestAnimationFrame(()=>this.resize());
  }

  close(){
    this.active=false;
    this.clock.stop();
    this.callbacks.onClose?.();
  }

  async loadRecord(item){
    const savedDesign=item.profile?.design||{};
    this.design=this.config.cloneDesign(savedDesign||this.config.catalog[0]);
    this.soundMode=item.profile?.sound?.mode==='mp3'?'mp3':'procedural';
    this.soundFile=item.audioFile||null;
    this.soundStats=item.profile?.sound||null;
    if(this.soundFileInput)this.soundFileInput.value='';
    if(this.soundFileName)this.soundFileName.textContent='Elegir archivo MP3';
    if(this.soundFileInfo)this.soundFileInfo.textContent=this.config.defaultSoundFileInfo;
    this.syncControls();
    this.rebuild();
    if(this.soundMode==='mp3'&&this.soundFile){
      const hasSavedLoopSettings=(
        Object.hasOwn(savedDesign,'sampleLoopStart')
        ||Object.hasOwn(savedDesign,'sampleLoopEndTrim')
      );
      await this.loadSoundFile(this.soundFile,{
        applySuggestedTrim:!hasSavedLoopSettings,
      });
    }
  }

  async save(){
    const fallback=this.config.fallbackId;
    const idInput=$(this.screen,`#${this.prefix}-editor-id`);
    const id=cleanId(idInput.value,fallback);
    idInput.value=id;
    if(this.soundMode==='mp3'&&!this.soundFile){
      this.setStatus('Cargá un MP3 antes de guardar el sonido importado.','warning');
      return;
    }
    this.saveButton.disabled=true;
    try{
      const result=await this.callbacks.onSave?.({
        id,
        design:this.config.cloneDesign(this.design),
        soundMode:this.soundMode,
        soundFile:this.soundFile,
        soundStats:this.soundStats,
      });
      this.setStatus(result?.message||this.config.savedMessage,'success');
    }catch(error){
      console.error(error);
      this.setStatus(error.message||this.config.errorMessage,'error');
    }finally{
      this.updateActionButtons();
    }
  }

  setStatus(message,state='ready'){
    this.status.textContent=message;
    this.status.dataset.state=state;
  }

  resize(){
    if(!this.active)return;
    const width=this.canvas.clientWidth;
    const height=this.canvas.clientHeight;
    if(!width||!height)return;
    this.renderer.setSize(width,height,false);
    this.camera.aspect=width/height;
    this.camera.updateProjectionMatrix();
  }

  animate(){
    requestAnimationFrame(()=>this.animate());
    if(!this.active)return;
    this.resize();
    const dt=Math.min(this.clock.getDelta(),.05);
    const time=performance.now()*.001;
    this.controls.update();
    if(this.config.autoMode==='rotate'&&this.autoInput.checked){
      this.previewMount.rotation.y+=dt*.22;
    }
    this.config.updatePreview(
      this.previewObject,
      this.design,
      dt,
      this.config.autoMode==='repeat'?this.autoInput.checked:time,
    );
    this.renderer.render(this.scene,this.camera);
  }
}

export class NitroEditor extends AnimatedEffectEditor{
  constructor(screen,callbacks={}){
    super(screen,{
      prefix:'nitro',
      catalog:NITRO_EFFECTS,
      fallbackId:'nitro-personalizado',
      cloneDesign:cloneNitroDesign,
      styleDefaults:nitroDefaults,
      createPreview:createNitroPreview,
      updatePreview:updateNitroPreview,
      autoSelector:'#nitro-editor-auto-rotate',
      autoStorageKey:AUTO_NITRO_STORAGE_KEY,
      autoMode:'rotate',
      cameraPosition:[3.9,2.5,4.7],
      target:[0,.65,.75],
      updatedMessage:'Vista de nitro actualizada. La estela se guarda como parámetros editables.',
      replayMessage:'Estela reiniciada.',
      testSound:previewNitroSound,
      loopSound:true,
      chooseSoundMessage:'Elegí un MP3 corto para repetirlo mientras se mantiene el boost.',
      loadingSoundMessage:'Analizando el loop MP3 de nitro…',
      loadedSoundMessage:'Loop MP3 validado. Se aplicarán recorte y fundido al repetir.',
      sampleSoundMessage:'Previsualización del loop MP3 de nitro reproducida.',
      defaultSoundFileInfo:'Loop corto y continuo mientras se mantiene el boost',
      soundMessage:'Previsualización de audio de nitro reproducida.',
      savedMessage:'Nitro guardado.',
      errorMessage:'No se pudo guardar el nitro.',
    },callbacks);
  }
}

export class ExplosionEditor extends AnimatedEffectEditor{
  constructor(screen,callbacks={}){
    super(screen,{
      prefix:'explosion',
      catalog:DEATH_EXPLOSIONS,
      fallbackId:'explosion-personalizada',
      cloneDesign:cloneExplosionDesign,
      styleDefaults:explosionDefaults,
      createPreview:createExplosionPreview,
      updatePreview:updateExplosionPreview,
      replay:resetExplosionPreview,
      autoSelector:'#explosion-editor-auto-repeat',
      autoStorageKey:AUTO_EXPLOSION_STORAGE_KEY,
      autoMode:'repeat',
      cameraPosition:[4,2.6,5],
      target:[0,.75,0],
      updatedMessage:'Vista de explosión actualizada. El efecto se reproduce automáticamente.',
      replayMessage:'Explosión reproducida nuevamente.',
      testSound:previewExplosionSound,
      loopSound:false,
      chooseSoundMessage:'Elegí un MP3 corto para usarlo como explosión.',
      loadingSoundMessage:'Analizando la explosión MP3…',
      loadedSoundMessage:'MP3 validado. Se reproducirá desde el comienzo en cada explosión.',
      sampleSoundMessage:'Previsualización de la explosión MP3 reproducida.',
      defaultSoundFileInfo:'Muestra corta y sin silencio inicial',
      soundMessage:'Previsualización de audio de explosión reproducida.',
      savedMessage:'Explosión guardada.',
      errorMessage:'No se pudo guardar la explosión.',
    },callbacks);
  }
}
